import { Kampos, effects, noise, utilities } from 'kampos';
import { angleToRad, lerp, getEffectReveal, sineInOut } from '../utilities/motion';
const getFrequency = (frequency, width, height, aspectRatio) => ({
  x: frequency / width * (1 + Math.max(0, aspectRatio)),
  // 1-1-2
  y: frequency / height * (1 - Math.min(0, aspectRatio)) // 2-1-1
});
export default async function create(target, options) {
  const {
    octaves = 3,
    angle = 0,
    isTriggeredByMouse = false,
    isTriggeredByScroll = false,
    intensity = 15
  } = options.namedEffect;
  let {
    frequency = 5,
    autoplay = true,
    aspectRatio = 0,
    speed = 5
  } = options.namedEffect;
  let playState = autoplay;
  const angleInRad = angleToRad(angle);
  const scrollString = `vec2 movement = u_mouse * u_resolution * vec2(cos(u_angle), sin(u_angle));
    vec3 turbulenceSeed = vec3(gl_FragCoord.xy - movement.xy, u_time * 0.0001);`;
  const turbulenceInput = isTriggeredByMouse ? effects.turbulence.FRAGCOORD_XY_MOUSE_TIME : isTriggeredByScroll ? scrollString : effects.turbulence.FRAGCOORD_XY_TIME;
  const height = target.height;
  const width = target.width;
  const SCALE = intensity / width;
  const turbulence = effects.turbulence({
    noise: noise.simplex,
    frequency: getFrequency(frequency, width, height, aspectRatio),
    octaves,
    isFractal: false,
    input: turbulenceInput,
    output: ''
  });
  turbulence.fragment.uniform.u_angle = 'float';
  turbulence.uniforms.push({
    name: 'u_angle',
    type: 'f',
    data: [angleInRad]
  });
  const turbulanceAngleIndex = turbulence.uniforms.length - 1;
  Object.defineProperty(turbulence, 'angle', {
    set: value => {
      turbulence.uniforms[turbulanceAngleIndex].data[0] = value;
    },
    get: () => turbulence.uniforms[turbulanceAngleIndex].data[0]
  });
  const mouse = utilities.mouse();
  const resolution = utilities.resolution({
    width: target.width,
    height: target.height
  });
  let lastProgress = 0;
  const displacement = effects.displacement({
    scale: {
      x: SCALE,
      y: SCALE
    },
    input: effects.displacement.TURBULENCE
  });
  const instance = new Kampos({
    effects: [resolution, mouse, turbulence, displacement],
    target
  });
  let _revealEffect;
  return {
    instance,
    play(_temp) {
      let {
        reveal,
        beforeDraw
      } = _temp === void 0 ? {} : _temp;
      if (reveal) {
        _revealEffect = getEffectReveal(progress => {
          const i = lerp(0, intensity, sineInOut(progress));
          this.setters.intensity(i);
        });
      }
      this.instance.play(beforeDraw);
    },
    beforeDraw(t) {
      if (playState) {
        turbulence.time = t * speed;
      }
      if (_revealEffect) {
        const done = _revealEffect(t);
        if (done) {
          _revealEffect = undefined;
        }
      }
    },
    setters: {
      autoplay: b => {
        autoplay = b;
        playState = autoplay;
      },
      speed(s) {
        speed = s;
      },
      frequency(f) {
        const target_ = instance.config.target;
        frequency = f;
        turbulence.frequency = getFrequency(f, target_.width, target_.height, aspectRatio);
      },
      octaves(o) {
        turbulence.octaves = o;
      },
      intensity(i) {
        const target_ = instance.config.target;
        displacement.scale = {
          x: i / (target_.width || 1),
          y: i / (target_.width || 1)
        };
      },
      mouse(p) {
        if (p.active === false) {
          return;
        }
        mouse.position = p;
        const hasVelocity = p.v.x || p.v.y;
        if (!autoplay) {
          if (playState && !hasVelocity) {
            playState = false;
          } else if (!playState && hasVelocity) {
            playState = true;
          }
        }
      },
      scroll(p) {
        const hasStopped = p === lastProgress;
        mouse.position = {
          x: p,
          y: p
        };
        if (!autoplay) {
          if (playState && hasStopped) {
            playState = false;
          } else if (!playState && !hasStopped) {
            playState = true;
          }
        }
        lastProgress = p;
      },
      scrollAngle(a) {
        turbulence.angle = angleToRad(a);
      },
      angle(a) {
        turbulence.angle = angleToRad(a);
      },
      aspectRatio(a) {
        const target_ = instance.config.target;
        aspectRatio = a;
        turbulence.frequency = getFrequency(frequency, target_.width, target_.height, a);
      }
    },
    resize(w, h) {
      instance.setSource({
        width: w,
        height: h
      });
      resolution.resolution = {
        width: w,
        height: h
      };
      turbulence.frequency = getFrequency(frequency, w, h, aspectRatio);
      displacement.scale = {
        x: intensity / w,
        y: intensity / w
      };
    }
  };
}
//# sourceMappingURL=Ripples.js.map